/**
 * TermsConditionsView Screen
 * Displays Terms and Conditions with acceptance information
 * IT Administrators can edit and update the T&C content
 * Accessible from the Settings menu
 */

import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  ActivityIndicator,
  Platform,
  TextInput,
  TouchableOpacity,
  Alert,
  Modal,
} from 'react-native';

import { Header } from '../components/Header';
import {
  FileText,
  CheckCircle,
  Edit,
  Save,
  X,
  AlertTriangle,
} from '../components/icons';
import {
  KeyboardDismissToolbar,
  ACCESSORY_VIEW_ID,
} from '../components/shared/KeyboardDismissToolbar';
import { SubHeader } from '../components/SubHeader';
import { useAuth } from '../contexts/AuthContext';
import {
  useCurrentTC,
  useAcceptanceInfo,
  useCreateTCVersion,
  usePublishTCVersion,
} from '../hooks/queries/useTermsConditions';
import { roleService } from '../services/auth/roleService';
import { dateFormatting } from '../utils/dateFormatting';

const isWeb = Platform.OS === 'web';

interface TermsConditionsViewProps {
  navigate: (path: string) => void;
  currentPath?: string;
}

export const TermsConditionsView: React.FC<TermsConditionsViewProps> = ({
  navigate,
  currentPath,
}) => {
  const { user } = useAuth();
  const {
    data: currentTC,
    isLoading: tcLoading,
    error: tcError,
    refetch,
  } = useCurrentTC();
  const { data: acceptanceInfo, isLoading: acceptanceLoading } =
    useAcceptanceInfo();

  // Admin mutations
  const createTCMutation = useCreateTCVersion();
  const publishTCMutation = usePublishTCVersion();

  // Check if user is IT Administrator
  const isITAdmin = roleService.isITAdministrator(user);

  // Edit mode state
  const [isEditing, setIsEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [showPublishConfirm, setShowPublishConfirm] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Initialize edit fields when entering edit mode
  useEffect(() => {
    if (isEditing && currentTC) {
      setEditContent(currentTC.content);
      setEditTitle(currentTC.title);
    }
  }, [isEditing, currentTC]);

  // Prevent body scroll when modal is open (web only)
  useEffect(() => {
    if (isWeb && showPublishConfirm) {
      // Save current overflow style
      const originalOverflow = document.body.style.overflow;
      // Prevent scrolling
      document.body.style.overflow = 'hidden';

      // Restore on cleanup
      return () => {
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [showPublishConfirm]);

  // Format date for display
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return 'Not accepted';
    return (
      dateFormatting.format(dateString, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }) ?? 'Not accepted'
    );
  };

  // Render HTML content (simplified)
  const renderContent = (htmlContent: string) => {
    const textContent = htmlContent
      .replace(/<h2>/gi, '\n\n')
      .replace(/<\/h2>/gi, '\n')
      .replace(/<h3>/gi, '\n\n')
      .replace(/<\/h3>/gi, '\n')
      .replace(/<p>/gi, '\n')
      .replace(/<\/p>/gi, '\n')
      .replace(/<li>/gi, '\n  - ')
      .replace(/<\/li>/gi, '')
      .replace(/<ul>/gi, '')
      .replace(/<\/ul>/gi, '\n')
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<strong>/gi, '')
      .replace(/<\/strong>/gi, '')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/&quot;/g, '"')
      .replace(/&#39;/g, "'")
      .replace(/\n{3,}/g, '\n\n')
      .trim();

    return textContent;
  };

  // Generate next version number
  const getNextVersion = (currentVersion: string): string => {
    const parts = currentVersion.split('.');
    if (parts.length === 3) {
      const patch = parseInt(parts[2], 10) + 1;
      return `${parts[0]}.${parts[1]}.${patch}`;
    }
    return `${currentVersion}.1`;
  };

  // Handle save and publish
  const handleSaveAndPublish = async () => {
    if (!currentTC) return;

    // Check if content actually changed
    if (editContent === currentTC.content && editTitle === currentTC.title) {
      setIsEditing(false);
      return;
    }

    setShowPublishConfirm(true);
  };

  // Confirm publish
  const confirmPublish = async () => {
    if (!currentTC) return;

    setIsSaving(true);
    setShowPublishConfirm(false);

    try {
      const newVersion = getNextVersion(currentTC.version);

      // Create new version
      const newTC = await createTCMutation.mutateAsync({
        version: newVersion,
        title: editTitle,
        content: editContent,
        effectiveDate: new Date().toISOString(),
      });

      // Publish the new version
      await publishTCMutation.mutateAsync(newTC.tcId);

      // Success
      setIsEditing(false);
      await refetch();

      if (isWeb) {
        alert(
          `Terms and Conditions updated to version ${newVersion}. All users will need to re-accept.`,
        );
      } else {
        Alert.alert(
          'Success',
          `Terms and Conditions updated to version ${newVersion}. All users will need to re-accept.`,
          [{ text: 'OK' }],
        );
      }
    } catch (error) {
      console.error('Error publishing T&C:', error);
      if (isWeb) {
        alert('Failed to update Terms and Conditions. Please try again.');
      } else {
        Alert.alert(
          'Error',
          'Failed to update Terms and Conditions. Please try again.',
        );
      }
    } finally {
      setIsSaving(false);
    }
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditContent('');
    setEditTitle('');
  };

  const isLoading = tcLoading || acceptanceLoading;

  return (
    <View style={styles.container}>
      <Header navigate={navigate} currentPath={currentPath} />
      <SubHeader title="Terms & Conditions" />

      {isLoading ? (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#FF6B35" />
          <Text style={styles.loadingText}>Loading...</Text>
        </View>
      ) : tcError || !currentTC ? (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>
            Unable to load Terms and Conditions
          </Text>
          {tcError?.message && (
            <Text style={styles.errorSubtext}>{tcError.message}</Text>
          )}
        </View>
      ) : (
        <ScrollView
          style={styles.content}
          contentContainerStyle={styles.scrollContent}
        >
          {/* IT Admin Banner */}
          {isITAdmin && (
            <View style={styles.adminBanner} testID="tc-admin-banner">
              <View style={styles.adminBannerContent}>
                <Edit size={20} color="#856404" />
                <Text style={styles.adminBannerText}>
                  As an IT Administrator, you can edit the Terms and Conditions
                  below.
                </Text>
              </View>
              {!isEditing ? (
                <TouchableOpacity
                  testID="tc-admin-edit-button"
                  style={styles.editButton}
                  onPress={() => setIsEditing(true)}
                >
                  <Edit size={16} color="#fff" />
                  <Text style={styles.editButtonText}>Edit</Text>
                </TouchableOpacity>
              ) : (
                <View style={styles.editActions}>
                  <TouchableOpacity
                    testID="tc-admin-cancel-button"
                    style={styles.cancelButton}
                    onPress={handleCancelEdit}
                    disabled={isSaving}
                  >
                    <X size={16} color="#6c757d" />
                    <Text style={styles.cancelButtonText}>Cancel</Text>
                  </TouchableOpacity>
                  <TouchableOpacity
                    testID="tc-admin-save-button"
                    style={[
                      styles.saveButton,
                      isSaving && styles.saveButtonDisabled,
                    ]}
                    onPress={handleSaveAndPublish}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <ActivityIndicator size="small" color="#fff" />
                    ) : (
                      <>
                        <Save size={16} color="#fff" />
                        <Text style={styles.saveButtonText}>
                          Save & Publish
                        </Text>
                      </>
                    )}
                  </TouchableOpacity>
                </View>
              )}
            </View>
          )}

          {/* Acceptance Info Banner */}
          <View style={styles.acceptanceBanner}>
            <View style={styles.acceptanceIconContainer}>
              <CheckCircle
                size={24}
                color={acceptanceInfo?.hasAccepted ? '#28a745' : '#6c757d'}
              />
            </View>
            <View style={styles.acceptanceDetails}>
              <Text style={styles.acceptanceTitle}>
                {acceptanceInfo?.hasAccepted ? 'Accepted' : 'Not Accepted'}
              </Text>
              <Text style={styles.acceptanceDate}>
                {acceptanceInfo?.hasAccepted
                  ? formatDate(acceptanceInfo.acceptedAt)
                  : 'You have not accepted the current terms.'}
              </Text>
            </View>
          </View>

          {/* Version Info */}
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <FileText size={20} color="#FF6B35" />
              {isEditing ? (
                <TextInput
                  testID="tc-admin-title-input"
                  style={styles.titleInput}
                  value={editTitle}
                  onChangeText={setEditTitle}
                  placeholder="Title"
                  placeholderTextColor="#999"
                  returnKeyType="done"
                />
              ) : (
                <Text style={styles.sectionTitle}>{currentTC.title}</Text>
              )}
            </View>
            <View style={styles.sectionMeta}>
              <Text style={styles.versionText}>
                Version {currentTC.version}
              </Text>
              <Text style={styles.effectiveDate}>
                Effective:{' '}
                {dateFormatting.format(currentTC.effectiveDate) ?? ''}
              </Text>
            </View>
          </View>

          {/* T&C Content */}
          <View style={styles.tcContainer}>
            {isEditing ? (
              <View>
                <Text style={styles.editLabel}>Content (HTML supported):</Text>
                <TextInput
                  testID="tc-admin-content-input"
                  style={styles.contentInput}
                  value={editContent}
                  onChangeText={setEditContent}
                  placeholder="Enter Terms and Conditions content..."
                  placeholderTextColor="#999"
                  multiline
                  textAlignVertical="top"
                  inputAccessoryViewID={
                    Platform.OS === 'ios' ? ACCESSORY_VIEW_ID : undefined
                  }
                />
                <Text style={styles.editHint}>
                  Supported HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;,
                  &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;br&gt;
                </Text>
              </View>
            ) : (
              <Text style={styles.tcContent}>
                {renderContent(currentTC.content)}
              </Text>
            )}
          </View>
        </ScrollView>
      )}

      {/* Publish Confirmation Modal - Outside ScrollView */}
      <Modal
        visible={showPublishConfirm}
        transparent={true}
        animationType="fade"
        onRequestClose={() => setShowPublishConfirm(false)}
        statusBarTranslucent={true}
        presentationStyle="overFullScreen"
      >
        <View style={styles.confirmOverlay}>
          <View style={styles.confirmModal}>
            <View style={styles.confirmHeader}>
              <AlertTriangle size={24} color="#dc3545" />
              <Text style={styles.confirmTitle}>Publish New Terms?</Text>
            </View>
            <Text style={styles.confirmText}>
              This will create a new version of the Terms and Conditions and
              publish it immediately.
            </Text>
            <View style={styles.warningBox}>
              <AlertTriangle size={20} color="#856404" />
              <Text style={styles.warningText}>
                All users will be required to re-accept the new terms before
                they can continue using the application.
              </Text>
            </View>
            <View style={styles.confirmActions}>
              <TouchableOpacity
                style={styles.confirmCancelButton}
                onPress={() => setShowPublishConfirm(false)}
              >
                <Text style={styles.confirmCancelText}>Cancel</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.confirmPublishButton}
                onPress={confirmPublish}
              >
                <Text style={styles.confirmPublishText}>Publish</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* iOS Keyboard Dismiss Toolbar */}
      {Platform.OS === 'ios' && isEditing && <KeyboardDismissToolbar />}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  errorText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#dc3545',
    marginBottom: 8,
  },
  errorSubtext: {
    fontSize: 14,
    color: '#666',
    textAlign: 'center',
    paddingHorizontal: 20,
  },
  content: {
    flex: 1,
  },
  scrollContent: {
    padding: 20,
    paddingBottom: 40,
  },
  // Admin Banner Styles
  adminBanner: {
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#ffeeba',
    padding: 16,
    marginBottom: 20,
    flexDirection: isWeb ? 'row' : 'column',
    alignItems: isWeb ? 'center' : 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
  },
  adminBannerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    flex: 1,
  },
  adminBannerText: {
    fontSize: 14,
    color: '#856404',
    flex: 1,
  },
  editButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FF6B35',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    gap: 8,
  },
  editButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  editActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  cancelButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#dee2e6',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    gap: 8,
  },
  cancelButtonText: {
    color: '#6c757d',
    fontWeight: '600',
    fontSize: 14,
  },
  saveButton: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#28a745',
    paddingVertical: 8,
    paddingHorizontal: 16,
    borderRadius: 6,
    gap: 8,
    minWidth: 140,
    justifyContent: 'center',
  },
  saveButtonDisabled: {
    opacity: 0.7,
  },
  saveButtonText: {
    color: '#fff',
    fontWeight: '600',
    fontSize: 14,
  },
  // Confirm Modal Styles
  confirmOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  confirmModal: {
    backgroundColor: '#fff',
    borderRadius: 12,
    padding: 24,
    maxWidth: 450,
    width: '100%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 20,
      },
      android: {
        elevation: 8,
      },
      web: {
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)' as any,
      },
    }),
  },
  confirmHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginBottom: 16,
  },
  confirmTitle: {
    fontSize: 20,
    fontWeight: '700',
    color: '#333',
  },
  confirmText: {
    fontSize: 15,
    color: '#666',
    lineHeight: 22,
    marginBottom: 16,
  },
  warningBox: {
    backgroundColor: '#fff3cd',
    borderRadius: 8,
    padding: 16,
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    marginBottom: 24,
  },
  warningText: {
    fontSize: 14,
    color: '#856404',
    flex: 1,
    lineHeight: 20,
  },
  confirmActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
  },
  confirmCancelButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#f8f9fa',
    borderWidth: 1,
    borderColor: '#dee2e6',
  },
  confirmCancelText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#6c757d',
  },
  confirmPublishButton: {
    paddingVertical: 10,
    paddingHorizontal: 20,
    borderRadius: 6,
    backgroundColor: '#dc3545',
  },
  confirmPublishText: {
    fontSize: 15,
    fontWeight: '600',
    color: '#fff',
  },
  // Edit Input Styles
  titleInput: {
    flex: 1,
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    borderWidth: 1,
    borderColor: '#dee2e6',
    borderRadius: 6,
    padding: 8,
    backgroundColor: '#fff',
  },
  editLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 8,
  },
  contentInput: {
    fontSize: 14,
    color: '#333',
    borderWidth: 1,
    borderColor: '#dee2e6',
    borderRadius: 6,
    padding: 12,
    backgroundColor: '#fff',
    minHeight: 400,
    fontFamily: isWeb ? 'monospace' : 'Courier',
    ...(isWeb && {
      whiteSpace: 'pre-wrap' as any,
    }),
  },
  editHint: {
    fontSize: 12,
    color: '#999',
    marginTop: 8,
    fontStyle: 'italic',
  },
  // Original Styles
  acceptanceBanner: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    padding: 16,
    marginBottom: 20,
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
  acceptanceIconContainer: {
    marginRight: 16,
    paddingTop: 2,
  },
  acceptanceDetails: {
    flex: 1,
  },
  acceptanceTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  acceptanceDate: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
    lineHeight: 20,
  },
  section: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    marginBottom: 20,
    overflow: 'hidden',
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e9ecef',
    gap: 12,
    backgroundColor: '#f8f9fa',
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  sectionMeta: {
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  versionText: {
    fontSize: 14,
    fontWeight: '500',
    color: '#FF6B35',
  },
  effectiveDate: {
    fontSize: 14,
    color: '#666',
  },
  tcContainer: {
    backgroundColor: '#fff',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#e9ecef',
    padding: 20,
  },
  tcContent: {
    fontSize: 15,
    lineHeight: 24,
    color: '#333',
    ...(isWeb && {
      whiteSpace: 'pre-wrap' as any,
    }),
  },
});

export default TermsConditionsView;
